import { create } from 'zustand';
import type { ProjectBulkPatch } from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import type { ProjectStatus } from '@shared/types';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';

export type ProjectsSort = 'recent' | 'deadline' | 'name';
export type ProjectEditorTab = 'general' | 'builder' | 'tasks';
export type ProjectBulkDraft = Omit<ProjectBulkPatch, 'projectIds'>;

interface ProjectSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
  bulkDraft: ProjectBulkDraft;
}

function createInitialProjectBulkDraft(): ProjectBulkDraft {
  return {
    status: { mode: 'unchanged' },
    customerId: { mode: 'unchanged' },
    deadlineAt: { mode: 'unchanged' },
    assigneeName: { mode: 'unchanged' },
    tags: { mode: 'unchanged' }
  };
}

function createInitialSelectionSession(): ProjectSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0,
    bulkDraft: createInitialProjectBulkDraft()
  };
}

interface ProjectsViewStore {
  statusFilter: 'all' | ProjectStatus;
  customerFilter: 'all' | string;
  sort: ProjectsSort;
  editorTab: ProjectEditorTab;
  selectionSession: ProjectSelectionSession;
  hydrateFromPreferences: () => void;
  setStatusFilter: (value: 'all' | ProjectStatus) => void;
  setCustomerFilter: (value: 'all' | string) => void;
  setSort: (value: ProjectsSort) => void;
  setEditorTab: (value: ProjectEditorTab) => void;
  setViewMode: (value: 'grid' | 'list') => Promise<void>;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
  updateBulkDraft: (update: Partial<ProjectBulkDraft>) => void;
  resetBulkDraft: () => void;
}

export const useProjectsViewStore = create<ProjectsViewStore>((set) => ({
  statusFilter: 'all',
  customerFilter: 'all',
  sort: 'recent',
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  hydrateFromPreferences: () => {
    useShellStore.getState();
  },
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCustomerFilter: (customerFilter) => set({ customerFilter }),
  setSort: (sort) => set({ sort }),
  setEditorTab: (editorTab) => set({ editorTab }),
  setViewMode: async (projectsViewMode) => {
    await useShellStore.getState().savePreferences({ projectsViewMode });
  },
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    })),
  updateBulkDraft: (update) =>
    set((state) => ({
      selectionSession: {
        ...state.selectionSession,
        bulkDraft: {
          ...state.selectionSession.bulkDraft,
          ...update
        }
      }
    })),
  resetBulkDraft: () =>
    set((state) => ({
      selectionSession: {
        ...state.selectionSession,
        bulkDraft: createInitialProjectBulkDraft()
      }
    }))
}));
